import { Component, DestroyRef, ViewEncapsulation, computed, inject, signal } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { MatDialog } from '@angular/material/dialog';
import { MatIconModule } from '@angular/material/icon';
import { MatTooltipModule } from '@angular/material/tooltip';
import { Router, RouterLink } from '@angular/router';
import { IconModule } from '@components/icons/icon.module';
import { LoadingComponent } from '@components/loading/loading.component';
import type { Vehicle, VehicleCfdSummary } from '@graphql/generated';
import { CfdApiService } from '@services/cfd-api.service';
import { ErrorHandlingService } from '@services/error-handling.service';
import { NotificationService } from '../../services/notification.service';
import { VehicleService } from '../../services/vehicle.service';
import {
  CopyVehicleDialogComponent,
  type CopyVehicleDialogData,
  type CopyVehicleDialogResult
} from '../vehicle-maintenance/copy-vehicle-dialog/copy-vehicle-dialog.component';
import {
  CopyVehicleCfdsDialogComponent,
  type CopyVehicleCfdsDialogData,
  type CopyVehicleCfdsDialogResult
} from '../vehicle-maintenance/vehicle-cfds/copy-vehicle-cfds-dialog/copy-vehicle-cfds-dialog.component';
import { CfdTileComponent } from './cfd-tile/cfd-tile.component';
import { groupFavoritesForDashboard } from './dashboard-grouping.util';
import type { DashboardVehicle, DashboardVehicleTile, DashboardYearGroup } from './dashboard.types';
import { OrderGuideNotesStore } from './order-guide-notes.store';
import {
  OrderGuideTileDialogComponent,
  type OrderGuideTileDialogData,
  type OrderGuideTileDialogResult
} from './order-guide-tile-dialog/order-guide-tile-dialog.component';
import { OrderGuideTileComponent } from './order-guide-tile/order-guide-tile.component';

/**
 * "My Dashboard" — the user's favorited vehicles as a pair of tiles each, grouped by model year and
 * then by region, with one region visible at a time. Order guide dates and CFD counts come from the
 * API; Order Guide notes and attachments are persisted in the CMT schema via {@link OrderGuideNotesStore}.
 */
@Component({
  selector: 'app-dashboard',
  encapsulation: ViewEncapsulation.None,
  imports: [
    CfdTileComponent,
    IconModule,
    LoadingComponent,
    MatIconModule,
    MatTooltipModule,
    OrderGuideTileComponent,
    RouterLink
  ],
  templateUrl: './dashboard.component.html',
  styleUrl: './dashboard.component.scss'
})
export class DashboardComponent {
  private readonly vehicleService = inject(VehicleService);
  private readonly cfdApi = inject(CfdApiService);
  private readonly errorService = inject(ErrorHandlingService);
  private readonly notify = inject(NotificationService);
  private readonly orderGuideNotes = inject(OrderGuideNotesStore);
  private readonly materialDialog = inject(MatDialog);
  private readonly router = inject(Router);
  private readonly destroyRef = inject(DestroyRef);

  readonly isLoading = signal(true);

  private readonly vehicles = signal<DashboardVehicle[]>([]);
  private readonly cfdSummaries = signal<VehicleCfdSummary[]>([]);

  readonly yearGroups = computed(() =>
    groupFavoritesForDashboard(
      this.vehicles(),
      this.cfdSummaries(),
      this.orderGuideNotes.notesMap(),
      this.orderGuideNotes.filesMap()
    )
  );

  /** Selected region per model year, keyed by year so each group remembers its own choice. */
  private readonly selectedRegionByYear = signal<Record<number, number>>({});

  /**
   * Model years the user has opened. Every year starts collapsed: a user with favorites across
   * several years would otherwise land on a wall of tiles.
   */
  private readonly expandedYears = signal<ReadonlySet<number>>(new Set());

  readonly hasFavorites = computed(() => this.yearGroups().some((g) => g.regions.length > 0));

  isExpanded(group: DashboardYearGroup): boolean {
    return this.expandedYears().has(group.modelYear);
  }

  toggleYear(group: DashboardYearGroup): void {
    this.expandedYears.update((current) => {
      const next = new Set(current);
      if (!next.delete(group.modelYear)) {
        next.add(group.modelYear);
      }
      return next;
    });
  }

  constructor() {
    this.load();
  }

  selectedRegionId(group: DashboardYearGroup): number | null {
    const selected = this.selectedRegionByYear()[group.modelYear];
    if (selected != null && group.regions.some((r) => r.regionId === selected)) {
      return selected;
    }
    return group.regions[0]?.regionId ?? null;
  }

  selectRegion(group: DashboardYearGroup, regionId: number): void {
    this.selectedRegionByYear.update((current) => ({ ...current, [group.modelYear]: regionId }));
    // Picking a region from a collapsed year is a request to see it.
    this.expandedYears.update((current) => new Set(current).add(group.modelYear));
  }

  visibleBrands(group: DashboardYearGroup) {
    const regionId = this.selectedRegionId(group);
    return group.regions.find((r) => r.regionId === regionId)?.brands ?? [];
  }

  /** Model year and region are repeated per card so a card read on its own is unambiguous. */
  vehicleLabel(tile: DashboardVehicleTile): string {
    const parts = [
      tile.vehicle.vehicleYear,
      tile.vehicle.regionName,
      tile.vehicle.vehicleName ?? `Vehicle ${tile.vehicle.id}`
    ];
    return parts
      .map((part) => part?.toString().trim())
      .filter((part): part is string => !!part)
      .join(' ');
  }

  trackByYear = (_index: number, group: DashboardYearGroup) => group.modelYear;
  trackByVehicleId = (_index: number, tile: DashboardVehicleTile) => tile.vehicle.id;

  /**
   * The dashboard only ever shows favorites, so the star is always on and can only be turned off.
   * The card is dropped locally on success rather than refetching, which would also re-run the CFD
   * count aggregate for every remaining vehicle.
   */
  onRemoveFavorite(tile: DashboardVehicleTile): void {
    const label = this.vehicleLabel(tile);
    this.vehicleService
      .toggleFavoriteVehicle(tile.vehicle.id)
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe({
        next: () => {
          this.vehicles.update((list) => list.filter((v) => v.id !== tile.vehicle.id));
          this.notify.success(`${label} removed from your dashboard`);
        },
        error: (err: unknown) => this.errorService.handleError(err, 'Could not update favorite')
      });
  }

  onEditOrderGuide(tile: DashboardVehicleTile): void {
    const data: OrderGuideTileDialogData = { tile };
    this.materialDialog
      .open(OrderGuideTileDialogComponent, {
        data,
        width: 'min(92vw, 48rem)',
        maxWidth: 'min(92vw, 48rem)',
        autoFocus: false
      })
      .afterClosed()
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe((result: OrderGuideTileDialogResult | undefined) => {
        if (!result) {
          return;
        }
        if (result.notes !== undefined) {
          this.orderGuideNotes
            .saveNotes(tile.vehicle.id, result.notes)
            .pipe(takeUntilDestroyed(this.destroyRef))
            .subscribe({
              error: (err: unknown) => this.errorService.handleError(err, 'Could not save notes')
            });
        }
        if (result.datesChanged) {
          this.load();
        }
      });
  }

  onCopyVehicle(tile: DashboardVehicleTile): void {
    const data: CopyVehicleDialogData = {
      currentVehicleId: tile.vehicle.id,
      currentVehicle: tile.vehicle as unknown as Vehicle
    };
    this.materialDialog
      .open(CopyVehicleDialogComponent, {
        data,
        panelClass: 'copy-vehicle-dialog-panel',
        width: 'min(80vw, 48rem)',
        maxWidth: 'min(80vw, 48rem)',
        autoFocus: false
      })
      .afterClosed()
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe((result: CopyVehicleDialogResult | false | undefined) => {
        if (result && result.copied) {
          void this.router.navigate(['/maintenance', result.newVehicleId]);
        }
      });
  }

  onCopyCfds(tile: DashboardVehicleTile): void {
    // The dialog only reads identity and display fields off the vehicle, all of which the favorites
    // query selects, so the narrower dashboard shape stands in for the full entity.
    const data: CopyVehicleCfdsDialogData = {
      currentVehicleId: tile.vehicle.id,
      currentVehicle: tile.vehicle as unknown as Vehicle
    };
    this.materialDialog
      .open(CopyVehicleCfdsDialogComponent, {
        data,
        panelClass: 'copy-vehicle-cfds-dialog-panel',
        width: 'min(90vw, 74rem)',
        maxWidth: 'min(90vw, 74rem)',
        autoFocus: false
      })
      .afterClosed()
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe((result: CopyVehicleCfdsDialogResult | false | undefined) => {
        if (result && result.copied) {
          this.load();
        }
      });
  }

  private load(): void {
    this.isLoading.set(true);

    // CFD counts follow favorites because they are keyed by the favorite ids.
    this.vehicleService
      .getFavoriteVehicles()
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe({
        next: (vehicles) => {
          const dashboardVehicles = vehicles as unknown as DashboardVehicle[];
          this.cfdSummaries.set([]);
          this.vehicles.set(dashboardVehicles);
          this.isLoading.set(false);
          const vehicleIds = dashboardVehicles.map((v) => v.id);
          this.orderGuideNotes.loadForVehicles(vehicleIds);
          this.loadCfdSummaries(vehicleIds);
        },
        error: (err: unknown) => {
          this.isLoading.set(false);
          this.errorService.handleError(err);
        }
      });
  }

  /**
   * Counts arrive after the tiles render: the aggregate walks every RPO of every favorite, so making
   * the whole dashboard wait on it would be the slowest part of the page.
   */
  private loadCfdSummaries(vehicleIds: readonly number[]): void {
    if (!vehicleIds.length) {
      return;
    }

    this.cfdApi
      .getVehicleCfdSummaries(vehicleIds)
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe({
        next: (summaries) => this.cfdSummaries.set(summaries),
        error: (err: unknown) => this.errorService.handleError(err)
      });
  }
}
